import React, { ForwardedRef } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/table/table.module.css';
import { DOMAttributes, SelectionMode } from '@react-types/shared';
import { RadioBox } from '../../Radio/src/RadioBox';
import { Checkbox } from '../../Checkbox/src/Checkbox';
import { AriaCheckboxProps } from '@react-aria/checkbox';
import { mergeProps } from '@react-aria/utils';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';

export type TableSelectionCellProps = Omit<
  DOMAttributes<HTMLTableCellElement>,
  'className' | 'style' | 'dangerouslySetInnerHTML'
> &
  SapphireStyleProps & {
    /**
     * Note: Currently, we don't officially support non-table elements.
     *
     * @default 'td'
     */
    elementType?: 'td';

    selectionMode: SelectionMode;
    disallowEmptySelection?: boolean;
    isDisabled?: boolean;

    checkboxProps?: AriaCheckboxProps;
  };

export const checkboxCellClasses = clsx(
  styles['sapphire-table__selectionCell'],
  styles['sapphire-table__cell--alignCenter']
);

/**
 * Select row cell, rendered in {@link Table.Row} instead of {@Link Table.Cell}
 * in selection column.
 */

export const TableSelectionCell = React.forwardRef(function TableSelectionCell(
  {
    elementType,
    selectionMode,
    disallowEmptySelection,
    isDisabled,
    checkboxProps,
    ...props
  }: TableSelectionCellProps,
  ref: ForwardedRef<HTMLTableCellElement>
) {
  const ElementType = elementType || 'td';
  const role = props.role || ElementType === 'td' ? undefined : 'cell';
  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  return (
    <ElementType
      {...mergeProps(filteredProps, { role })}
      className={clsx(checkboxCellClasses, styleProps.className)}
      style={styleProps.style}
      ref={ref}
    >
      {selectionMode === 'single' && disallowEmptySelection ? (
        <RadioBox {...checkboxProps} role="checkbox" isDisabled={isDisabled} />
      ) : (
        selectionMode !== 'none' && (
          <Checkbox
            INTERNAL_slot="selection"
            {...checkboxProps}
            isDisabled={isDisabled}
          />
        )
      )}
    </ElementType>
  );
});
